import React from 'react';
import { RemoteAlertGroup, AlertSchedule } from '../../types/remoteAlerts';
import { Bell, Clock } from 'lucide-react';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatScheduleSummary(s: AlertSchedule): string {
  if (!s.enabled) return '';

  // Support both new {windows} and legacy {days,startTime,endTime} shapes
  let windows = s.windows as any[];
  if (!Array.isArray(windows) && Array.isArray((s as any).days)) {
    const old = s as any;
    windows = old.days.map((d: number) => ({ day: d, start: old.startTime, end: old.endTime }));
  }
  if (!windows || windows.length === 0) return '';

  const activeDays = [...new Set(windows.map((w: any) => w.day))].sort((a, b) => a - b);
  const totalWindows = windows.length;

  const dayStr =
    activeDays.length === 7 ? 'Daily'
    : activeDays.join(',') === '1,2,3,4,5' ? 'Weekdays'
    : activeDays.join(',') === '0,6' ? 'Weekends'
    : activeDays.map(d => DAY_NAMES[d]).join(', ');

  // If every day has exactly one window and they're all the same, show the time range
  const allSame = windows.every((w: any) => w.start === windows[0].start && w.end === windows[0].end);
  if (allSame && totalWindows === activeDays.length) {
    return `${dayStr} · ${windows[0].start}–${windows[0].end}`;
  }

  return `${dayStr} · ${totalWindows} window${totalWindows !== 1 ? 's' : ''}`;
}

interface RemoteAlertGroupTableProps {
  alertGroups: RemoteAlertGroup[];
  onAddGroup: () => void;
  onEditGroup: (group: RemoteAlertGroup) => void;
  onDeleteGroup: (groupId: string) => void;
  canManage?: boolean;
}

const RemoteAlertGroupTable: React.FC<RemoteAlertGroupTableProps> = ({ alertGroups, onAddGroup, onEditGroup, onDeleteGroup, canManage = false }) => {
  return (
    <div className="bg-app-panel dark:bg-gray-800 shadow-md rounded-xl border border-gray-200 dark:border-gray-700 min-w-0 max-w-full">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Alert Groups</h2>
        {canManage && (
          <button
            type="button"
            onClick={onAddGroup}
            className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <span className="hidden sm:inline">Add Group</span>
            <span className="sm:hidden">+ Add</span>
          </button>
        )}
      </div>

      {alertGroups.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
          No alert groups yet. Press <strong>Add Group</strong> to create one.
        </div>
      ) : (
        <>
          {/* Mobile: card list */}
          <ul className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
            {alertGroups.map((group) => (
              <li key={group.id} className="px-4 py-4 flex items-start justify-between gap-3 min-w-0">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/20 rounded flex items-center justify-center">
                      <Bell className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{group.name}</span>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 pl-8">
                    {(group.sensorIds ?? []).length} sensor{(group.sensorIds ?? []).length !== 1 ? 's' : ''}
                    <span className="mx-1.5">·</span>
                    {(group.recipients ?? []).length} recipient{(group.recipients ?? []).length !== 1 ? 's' : ''}
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-1 pl-8">
                    {(group.notifyTemperature ?? true) && <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">Temp</span>}
                    {(group.notifyBattery ?? true) && <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Battery</span>}
                    {(group.notifyMissingData ?? true) && <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">Missing</span>}
                    {group.schedule?.enabled
                      ? <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"><Clock className="h-2.5 w-2.5" />{formatScheduleSummary(group.schedule)}</span>
                      : <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">Always on</span>
                    }
                  </div>
                </div>
                {canManage && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => onEditGroup(group)}
                      className="px-2.5 py-1.5 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteGroup(group.id)}
                      className="px-2.5 py-1.5 text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                    >
                      Del
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>

          {/* Desktop: table */}
          <div className="hidden md:block overflow-x-auto min-w-0 max-w-full overscroll-x-contain">
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-6 py-3">Name</th>
                  <th scope="col" className="px-6 py-3">Sensors</th>
                  <th scope="col" className="px-6 py-3">Recipients</th>
                  <th scope="col" className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {alertGroups.map((group) => (
                  <tr key={group.id} className="bg-app-panel border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900 dark:text-white whitespace-nowrap">{group.name}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(group.notifyTemperature ?? true) && <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">Temp</span>}
                        {(group.notifyBattery ?? true) && <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Battery</span>}
                        {(group.notifyMissingData ?? true) && <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">Missing</span>}
                        {group.schedule?.enabled
                          ? <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"><Clock className="h-2.5 w-2.5" />{formatScheduleSummary(group.schedule)}</span>
                          : <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">Always on</span>
                        }
                      </div>
                    </td>
                    <td className="px-6 py-4">{(group.sensorIds ?? []).length}</td>
                    <td className="px-6 py-4">{(group.recipients ?? []).length}</td>
                    <td className="px-6 py-4 text-right">
                      {canManage && (
                        <>
                          <button
                            type="button"
                            onClick={() => onEditGroup(group)}
                            className="text-blue-600 dark:text-blue-500 hover:underline"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => onDeleteGroup(group.id)}
                            className="text-red-600 dark:text-red-500 hover:underline ml-4"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default RemoteAlertGroupTable;
